<template>
  <div class="experts">
    <header class="head"> 
      <input class="search" v-model="search" :placeholder="$t('randevu.experts.search_placeholder')" />
      <button class="primary" @click="openModal('ExpertAdd')">
        <img class="icon" src="../icons/plusw.svg" /> {{ $t('randevu.experts.new') }}
      </button>
    </header>

    <div v-if="loading" class="info">{{ $t('common.loading') }}</div>
    <div v-if="error"  class="error">{{ error }}</div>

    <div v-if="filtered.length" class="card-list">
      <div v-for="e in filtered" :key="e.id" class="service-card">
        <div class="card-body">
          <h3>{{ e.name }}</h3>
          <p><strong>{{ $t('randevu.experts.email_label') }}</strong> {{ e.email || '-' }}</p>
          <p><strong>{{ $t('randevu.experts.phone_label') }}</strong> {{ e.phone || '-' }}</p>
        </div>
        <div class="card-actions">
          <button class="btn-icon" @click="openModal('ExpertDetail', e.id)">
            <img src="../icons/goruntule.svg" :alt="$t('randevu.experts.view_alt')" />
          </button>
          <button class="btn-icon" @click="openModal('ExpertAdd', e)">
            <img src="../icons/duzenle.svg" :alt="$t('randevu.experts.edit_alt')" />
          </button>
          <button class="btn-icon" @click="confirmDelete(e.id)">
            <img src="../icons/sil.svg" :alt="$t('randevu.experts.delete_alt')" />
          </button>
        </div>
      </div>
    </div>

    <div v-if="!filtered.length && !loading && !error" class="info">{{ $t('randevu.experts.empty') }}</div>

    <div v-if="showModal" class="modal-overlay" @click="overlay">
      <div ref="modal" class="modal-content" :style="style" @click.stop>
        <div class="close-div"><button class="close-button" @click="close">X</button></div>
        <component
          :is="current"
          :expert="selected"
          :expertId="selectedId"
          @refresh="fetch"
          @close="close"
        />
      </div>
    </div>
  </div>
</template>


<script>
import { ref, computed, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import axios from "axios";
import ExpertAdd from "./ExpertAdd.vue";
import ExpertDetail from "./ExpertDetail.vue";

export default {
  name: "MyExperts",
  components: { ExpertAdd, ExpertDetail },
  setup() {
    const { t } = useI18n();
    const experts  = ref([]);
    const loading  = ref(false);
    const error    = ref(null);

    const search   = ref("");
    const filtered = computed(() =>
      experts.value.filter(e => e.name.toLowerCase().includes(search.value.toLowerCase()))
    );

    const showModal  = ref(false);
    const current    = ref(null);
    const selected   = ref(null);
    const selectedId = ref(null);
    const style      = ref({ transform: "translateY(100%)", transition: "transform .3s ease" });

    const fetch = async () => {
      loading.value = true;
      error.value = null;
      try {
        const { data } = await axios.get("/promo/user/my-experts");
        experts.value = data;
      } catch (err) {
        if (err.response && err.response.status === 404) {
          error.value = t("randevu.experts.empty_404");
          experts.value = [];
        } else {
          error.value = t("randevu.experts.fetch_error");
        }
      } finally {
        loading.value = false;
      }
    };

    const openModal = (comp, expert = null) => {
      current.value = comp;
      selected.value = typeof expert === "object" ? expert : null;
      selectedId.value = typeof expert === "number" ? expert : null;
      showModal.value = true;
      nextTick(() => setTimeout(() => (style.value.transform = "translateY(0)"), 10));
    };

    const close   = () => { style.value.transform = "translateY(100%)"; setTimeout(() => { showModal.value = false; current.value = null; }, 400); };
    const overlay = e => { if (e.target === e.currentTarget) close(); };

    const confirmDelete = async id => {
      if (!confirm(t("randevu.experts.delete_confirm"))) return;
      try {
        await axios.delete(`/promo/user/my-experts/${id}`);
        await fetch();
      } catch {
        error.value = t("randevu.experts.delete_error");
      }
    };

    onMounted(fetch);
    return { experts, loading, error, search, filtered, openModal, showModal,
             current, selected, selectedId, style, close, overlay, confirmDelete, fetch };
  }
};
</script>

<style scoped> 
.head {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 1em;
}
.search {
  flex: 1 1 180px;
  padding: 8px;
}
.primary {
  background: linear-gradient(45deg, #2b2b2b, #2b2b2b, #003615); 
  border: solid 1px #2b2b2b;
  cursor: pointer;
  border-radius: 0;
  transition: all 0.3s ease; 
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 24px; 
}
.primary:hover {
  border-color: #2b2b2b;
  color: #ffffff;
  transform: scale(1.05); 
}
.icon {
  max-width: 1rem;
}

.card-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1em;
}

.service-card {
  background: linear-gradient(145deg, #050505, #0a0a0a);
  border: 1px solid #1a1a1a;
  border-radius: 4px;
  padding: 1em;
  color: #ddd;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

h3 {
  font-size: 1.3em;
  font-weight: 300;
  margin: 3px;
}
p {
  margin: 3px;
}
.card-body {
  flex: 1;
}
.card-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
}
.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
}
.btn-icon img {
  width: 20px;
  height: 20px;
  filter: brightness(1.2); 
  transition: all 0.2s ease; 
}
.btn-icon:hover img {
  transform: scale(1.1);
  filter: brightness(1.5);
}
.info {
  margin-top: 12px;
  color: #aaa;
}
.error {
  font-weight: 500;
  margin-top: 12px;
  color: #917e5c;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
}
.modal-content {
  margin-top: 12rem;
  width: 100%;
  max-width: 1080px;
  background: linear-gradient(45deg, #020202, #080808, #030b0b);
  border-radius: 10px 10px 0 0;
  border-top: 1px solid #022121;
  border-left: 1px solid #022121;
  border-right: 1px solid #022121;
  padding: 1rem;
  overflow-y: auto;
  max-height: 80vh;
}
.close-div {
  position: sticky;
  border-radius: 15px 15px 0 0;
  top: 0;
  display: flex;
  justify-content: flex-end;
  z-index: 1;
  padding: 0.5rem;
}

.close-button {
  background-color: #000000;
  color: #ffffff;
  padding: 0.8rem;
  min-width: 3rem;
  max-width: 3rem;
  border-radius: 2rem;
  opacity: 0.8;
  border: 1px solid #444444;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.close-button:hover {
  opacity: 1;
  transform: scale(1.1);
}
</style>

